import {BaseIdModel} from "common-shared";
import {Exclude, Transform, Type} from "class-transformer";
import {ExpressionModel} from "../../common/expression.model";
import {IIdCode} from "common-shared";

export class SpecSource {
  Type: string;
  Id: string;
  Code: string;

  constructor(init?: Partial<SpecSource>) {
    Object.assign(this, init);
  }
}

export interface IWithSpecs extends IIdCode {
  Specs: SpecLineModel[];
}

export class WithSpecsModel extends BaseIdModel {
  @Type(() => SpecLineModel)
  Specs: SpecLineModel[];
}

export class SpecLineModel extends BaseIdModel {
  PropertyId: string;
  UnitId?: string;
  NumericValue?: number;
  @Type(() => Date)
  DateTimeValue?: Date;
  ValueClv?: string;
  Value: string;
  LanguageId?: string;
  MappingId?: string;
  LineId?: string;
  @Type(() => ExpressionModel)
  Expression: ExpressionModel = new ExpressionModel();
  @Type(() => ExpressionModel)
  Constraints: ExpressionModel = new ExpressionModel();
  @Type(() => ExpressionModel)
  Mandatory: ExpressionModel = new ExpressionModel();
  @Type(() => ExpressionModel)
  AliasFormula: ExpressionModel = new ExpressionModel();
  @Type(() => ExpressionModel)
  MessageFormula: ExpressionModel = new ExpressionModel();
  @Type(() => ExpressionModel)
  Trace: ExpressionModel = new ExpressionModel();
  MandatoryClv: string;
  Sequence?: number;
  GroupClv: string;
  Alias?: string;
  Message?: string;
  @Exclude()
  $isNew: boolean = false;
  $original?: any;
  @Type(() => SpecSource)
  LineSource: SpecSource;

  @Type(() => SpecSource)
  MappingSource: SpecSource;

  @Transform(({obj}) => obj.ParsedConstraints || [], {toClassOnly: true})
  ParsedConstraints: any[] = [];
  Default: any;
  @Transform(() => null, {toClassOnly: true})
  Previous: any;
  @Exclude()
  ShowLocalization: boolean = false;
  @Exclude()
  RowGroupKey: string = '-';
  @Exclude()
  Group: string;
  OriginClv: string;
  PriorityClv: string;

  get SourceId(): string {
    return (this.LineSource || this.MappingSource || {Id: undefined}).Id;
  }

  constructor(init?: Partial<SpecLineModel>) {
    super();
    Object.assign(this, init);
    this.LineSource = new SpecSource();
    this.MappingSource = new SpecSource();
  }

  ensureSources(Id: string, Code: string) {

    this.resetLineSource();
    if (this.LineSource && this.LineSource.Id == null) {
      this.LineSource = new SpecSource({Id: Id, Code: Code});
    }

    this.resetMappingSource();
    if (this.MappingSource && this.MappingSource.Id == null) {
      this.MappingSource = new SpecSource({Id: Id, Code: Code});
    }
  }

  resetMappingSource(init?: Partial<SpecSource>) {
    init = init ?? {Id: null}
    this.MappingSource = new SpecSource(init);
  }

  resetLineSource(init?: Partial<SpecSource>) {
    init = init ?? {Id: null}
    this.LineSource = new SpecSource(init);
  }

  resetValue() {
    this.Value = null;
    this.ValueClv = null;
    this.DateTimeValue = null;
    this.NumericValue = null;
  }
  resetMapping() {
    this.MandatoryClv = null;
    this.Expression = new ExpressionModel();
    this.Constraints = new ExpressionModel();
    this.Mandatory = new ExpressionModel();
    this.AliasFormula = new ExpressionModel();
    this.GroupClv = null;
  }

  hasValue(): boolean {
    return !!this.Value || !!this.ValueClv || (this.NumericValue !== undefined && this.NumericValue !== null) || !!this.DateTimeValue;
  }

  hasMappingValue(): boolean {
    return !!this.Expression.hasFormula()
      || !!this.Constraints.hasFormula()
      || !!this.Mandatory.hasFormula()
      || !!this.AliasFormula.hasFormula() || !!this.Alias
      || !!this.MessageFormula.hasFormula() || !!this.Message
      || !!this.GroupClv
      || !!this.PriorityClv;
  }

  isValueInvalid(): boolean {
    return !this.hasValue() && !this.MandatoryClv && !this.hasMappingValue();
  }

  isVisibilityInvalid(): boolean {
    return !this.hasValue() && !this.MandatoryClv || (!this.MandatoryClv && this.hasMappingValue());
  }

  isValid() {
    return !this.isValueInvalid() && !this.isVisibilityInvalid();
  }

  update(updateTo: SpecLineModel) {
    updateTo.Value = this.Value;
    updateTo.ValueClv = this.ValueClv;
    updateTo.NumericValue = this.NumericValue;
    updateTo.DateTimeValue = this.DateTimeValue;
  }

  getValue() {
    return this.Value ?? this.ValueClv ?? this.NumericValue ?? this.DateTimeValue;
  }

  static EnsureValue(specs: SpecLineModel[], propertyId: string, value: Partial<SpecLineModel>){
    let specLineModel = specs.find(value1 => value1.PropertyId == propertyId);
    if(specLineModel == null){
      specLineModel = new SpecLineModel({PropertyId: propertyId});
      specs = [...specs, specLineModel];
    }
    Object.assign(specLineModel, value);
    return specs;
  }

  static getSpec(specs: SpecLineModel[], propertyId: string) {
    return specs?.find(value => value.PropertyId?.toLocaleLowerCase() === propertyId?.toLocaleLowerCase());
  }
}

export class SpecLineFilterModel extends SpecLineModel {
  ValueClvs: string[] = []

  resetValue() {
    super.resetValue();
    this.ValueClvs = [];
  }
}
